/**
 * What the Games tab says about a page that offers no chips: still loading, "No game lists on this page", or that the
 * page's lists could not be loaded.
 *
 * "No game lists on this page" is a verdict, so it needs an answer behind it. It used to come from a timer: once the
 * site bridge's first, quick poll ran out (15s), a page whose brand site had not reported its lists yet read as having
 * none, and the lists popped in seconds later. A read that failed counted as an answer too. Now the page is empty only
 * once a read that completed says so, and a read that failed says the lists could not be loaded.
 */

/** How the service's read of the brand's site pages ended (`getSitePages`): `unavailable` is a service that cannot
 *  say (not configured, or without the route), which is not a failure. */
export type SitePagesRead = 'pending' | 'ok' | 'unavailable' | 'failed';

/** What the page's structure says of its game lists: it has `some`, it has `none`, the read that would say is still
 *  on its way (`pending`), or nothing can say (`unknown`), and the site's own report is all there is. */
export type PageStructure = 'pending' | 'some' | 'none' | 'unknown';

export type PageStructureInput = {
  /** The page's kind, as the service names it (a page of sections, a category page, a single-list page). */
  kind: 'sections' | 'category' | 'list';
  /** The lists the page places: those its sections show as the site serves them, and those its arrangement names. */
  placedLists: number;
  /** The page's sections have been read from the site. */
  sectionsRead: boolean;
  /** The page's arrangement has answered, or the page has none to wait for. */
  arrangementIn: boolean;
  /** How the read of the site pages ended. */
  sitePages: SitePagesRead;
  /** That read names this page as one that shows game lists. */
  listed: boolean;
};

/**
 * What the page's structure says of its lists. The service names the pages that hold game lists: one it does not name
 * has none, and a category or list page it names has some. A page of sections it names only has a SOURCE of lists:
 * whether those sections place any is read from the site (its sections, and the page's arrangement).
 */
export const pageStructure = ({
  kind,
  placedLists,
  sectionsRead,
  arrangementIn,
  sitePages,
  listed,
}: PageStructureInput): PageStructure => {
  if (placedLists > 0) return 'some';
  if (sitePages === 'pending') return 'pending';
  if (sitePages === 'ok' && !listed) return 'none';
  if (kind === 'sections' && sectionsRead) return arrangementIn ? 'none' : 'pending';
  return sitePages === 'ok' ? 'some' : 'unknown';
};

/** While the page offers no chips: the skeleton, the page's "no game lists", or that its lists could not be loaded. */
export type PageListsVerdict = 'loading' | 'empty' | 'failed';

export type PageListsVerdictInput = {
  /** The site bridge is connected to the page. */
  connected: boolean;
  /** The site's first, quick report is over (the bridge's `sourcesSettled`). */
  siteSettled: boolean;
  /** The site has said all it will for the page: its sources held, or the slow poll after the quick one ran out too
   *  (the bridge's `sourcesDone`). */
  siteDone: boolean;
  /** What the page's structure says of its lists (`pageStructure`). */
  structure: PageStructure;
  /** A read the page's lists depend on failed: the site pages, the page's arrangement, or the brand's own lists. */
  readFailed: boolean;
};

/**
 * The verdict for a page that offers no chips. Nothing is said before the site's first report is over, as before. Then
 * the page's lists are known once its structure says it has none, or once the site has said all it will (a site can
 * report its lists long after its first report, so until then the skeleton stays). Known, a failed read says the lists
 * could not be loaded; so does a page whose structure has lists the site never reported. Otherwise the page is empty.
 */
export const pageListsVerdict = ({
  connected,
  siteSettled,
  siteDone,
  structure,
  readFailed,
}: PageListsVerdictInput): PageListsVerdict => {
  if (!connected || !siteSettled) return 'loading';
  if (structure !== 'none' && !siteDone) return 'loading';
  if (readFailed) return 'failed';
  if (structure === 'pending') return 'loading';
  return structure === 'some' ? 'failed' : 'empty';
};
